import React, { useState, useEffect, useMemo } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation, faCopy } from '@fortawesome/free-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import {
    registerUser,
    replaceRolesAndEntities,
    getEntitiesByType,
} from '../../../redux/slices/userSlice';
import CreateUserModal from './CreateUserModal';

const generatePassword = (length = 6) => {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*+';
    return Array.from(crypto.getRandomValues(new Uint32Array(length)))
        .map(x => charset[x % charset.length])
        .join('');
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEBOUNCE_MS = 500;

const findCorrectKey = (e) => {
    if (e?.EntityType === 2) return e.EntityID;
    if (e?.EntityType === 3) return e.ChainID;
    return e.ClubID;
};

const entityKey = (e) => `${e?.EntityType}:${findCorrectKey(e)}`;

const NewUser = () => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.user);
    const entityData = useSelector((state) => state.user.entityData);
    const isUKUser = user?.CountryID === 2;

    // ── Modal visibility ─────────────────────────────────────────────────────────
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [createdEmail, setCreatedEmail] = useState('');
    const [createdPassword, setCreatedPassword] = useState('');
    const [copyMessage, setCopyMessage] = useState('');

    // ── Create form state ────────────────────────────────────────────────────────
    const [email, setEmail] = useState('');
    const [customerCode, setCustomerCode] = useState('');
    const [password, setPassword] = useState('');
    const [updatedRoles, setUpdatedRoles] = useState([]);
    const [entityTree, setEntityTree] = useState([]);
    const [addedEntities, setAddedEntities] = useState([]);
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    // ── Entity search state ──────────────────────────────────────────────────────
    const [isAddToggled, setIsAddToggled] = useState(true);
    const [isNetworksActive, setNetworksActive] = useState(false);
    const [isChainsActive, setChainsActive] = useState(false);
    const [isClubsActive, setClubsActive] = useState(true);
    const [countryIdFilter, setCountryIdFilter] = useState(isUKUser ? 2 : 1);
    const [searchEntityName, setSearchEntityName] = useState('');
    const [searchEntityID, setSearchEntityID] = useState('');
    const [debouncedSearchEntityName, setDebouncedSearchEntityName] = useState('');
    const [debouncedSearchEntityID, setDebouncedSearchEntityID] = useState('');

    // ── Derived ──────────────────────────────────────────────────────────────────
    const addedKeys = useMemo(() => new Set(addedEntities.map(entityKey)), [addedEntities]);
    const isEntityAdded = (e) => addedKeys.has(entityKey(e));

    const isValid = emailPattern.test(email.trim());

    // ── Open / close ─────────────────────────────────────────────────────────────
    const openCreateModal = () => {
        setPassword(generatePassword());
        setEmail('');
        setCustomerCode('');
        setUpdatedRoles([]);
        setEntityTree([]);
        setAddedEntities([]);
        setErrors({});
        setIsAddToggled(true);
        setNetworksActive(false);
        setChainsActive(false);
        setClubsActive(true);
        setSearchEntityName('');
        setSearchEntityID('');
        setCountryIdFilter(isUKUser ? 2 : 1);
        setShowCreateModal(true);
    };

    const closeCreateModal = () => {
        setShowCreateModal(false);
        setSearchEntityName('');
        setSearchEntityID('');
    };

    // ── Entity search debounce ────────────────────────────────────────────────────
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearchEntityName(searchEntityName), DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [searchEntityName]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearchEntityID(searchEntityID), DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [searchEntityID]);

    // fire entity search when modal is open and search terms / type changes
    useEffect(() => {
        if (!showCreateModal) return;
        let entityTypeStr = 'club';
        if (isNetworksActive) entityTypeStr = 'network';
        if (isChainsActive) entityTypeStr = 'chain';

        dispatch(getEntitiesByType({
            userId: user?.UserID ?? 0,
            entityType: entityTypeStr,
            searchLike: debouncedSearchEntityName || null,
            searchId: +debouncedSearchEntityID || null,
            countryId: countryIdFilter,
            maxResults: 250,
        }));
    }, [showCreateModal, isNetworksActive, isChainsActive, isClubsActive, debouncedSearchEntityName, debouncedSearchEntityID, countryIdFilter]);

    // ── Entity panel handlers ────────────────────────────────────────────────────
    const handleBtnGroupChange = (type) => {
        setSearchEntityName('');
        setSearchEntityID('');
        setNetworksActive(type === 'network');
        setChainsActive(type === 'chain');
        setClubsActive(type === 'club');
    };

    const handleAdd = (entity) => {
        if (!entity) return;
        const key = entityKey(entity);
        setAddedEntities((prev) => prev.some((x) => entityKey(x) === key) ? prev : [...prev, entity]);
        setEntityTree((prev) => prev.some((x) => entityKey(x) === key) ? prev : [...prev, entity]);
    };

    const handleRemove = (entity) => {
        if (!entity) return;
        const key = entityKey(entity);
        setAddedEntities((prev) => prev.filter((x) => entityKey(x) !== key));
        setEntityTree((prev) => prev.filter((x) => entityKey(x) !== key));
    };

    // ── Submit ───────────────────────────────────────────────────────────────────
    const handleSave = async () => {
        const trimmedEmail = email.trim();
        const newErrors = {};
        if (!emailPattern.test(trimmedEmail)) newErrors.email = 'Please enter a valid email address';
        if (Object.keys(newErrors).length) { setErrors(newErrors); return; }

        setIsSaving(true);
        try {
            const registerAction = await dispatch(registerUser({
                Email: trimmedEmail,
                Password: password,
                AdvertiserCode: customerCode.trim() || null,
            }));

            if (!registerUser.fulfilled.match(registerAction)) {
                const msg = registerAction.payload ?? 'Failed to create user';
                setErrors({ email: msg });
                return;
            }

            const newUserId = registerAction.payload?.UserID;

            const entitiesCsv = entityTree
                .map((e) => `${e.EntityType}:${findCorrectKey(e)}`)
                .join(',');
            const rolesCsv = updatedRoles.join(',');

            await dispatch(replaceRolesAndEntities({
                userId: newUserId,
                newEmail: trimmedEmail,
                rolesCsv,
                entitiesCsv,
            }));

            setCreatedEmail(trimmedEmail);
            setCreatedPassword(password);
            closeCreateModal();
            setShowSuccessModal(true);
        } finally {
            setIsSaving(false);
        }
    };

    // ── Copy password ────────────────────────────────────────────────────────────
    const handleCopyPassword = () => {
        navigator.clipboard.writeText(createdPassword)
            .then(() => {
                setCopyMessage('Password copied to clipboard!');
                setTimeout(() => setCopyMessage(''), 3000);
            })
            .catch(() => setCopyMessage('Failed to copy password.'));
    };

    return (
        <>
            <div className="container">
                <div className="row d-flex align-items-center justify-content-center">
                    <div className="w-100 mt-4">
                        <Button
                            className="btn-lg w-100"
                            variant="primary"
                            onClick={openCreateModal}
                        >
                            Create Account
                        </Button>
                    </div>
                </div>
            </div>

            <CreateUserModal
                show={showCreateModal}
                onClose={closeCreateModal}

                email={email}
                onEmailChange={setEmail}
                customerCode={customerCode}
                onCustomerCodeChange={setCustomerCode}
                temporaryPassword={password}
                errors={errors}

                updatedRoles={updatedRoles}
                onRolesChange={setUpdatedRoles}

                entityTree={entityTree}
                onRemoveEntity={handleRemove}

                isAddToggled={isAddToggled}
                onToggleAddMode={() => setIsAddToggled((p) => !p)}

                isNetworksActive={isNetworksActive}
                isChainsActive={isChainsActive}
                isClubsActive={isClubsActive}
                handleBtnGroupChange={handleBtnGroupChange}

                entityAddData={entityData}
                entitySearchLike={searchEntityName}
                onEntitySearchLikeChange={setSearchEntityName}
                entityIDSearch={searchEntityID}
                onEntityIDSearchChange={setSearchEntityID}

                countryIdFilter={countryIdFilter}
                onCountryIdFilterChange={(id) => {
                    setCountryIdFilter(id);
                    if (id === 1) {
                        setNetworksActive(false);
                        setChainsActive(false);
                        setClubsActive(true);
                    }
                }}

                onAddEntity={handleAdd}
                isEntityAdded={isEntityAdded}

                onSave={handleSave}
                isValid={isValid}
                isSaving={isSaving}

                isUKUser={isUKUser}
            />

            {/* Success modal */}
            <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon className="text-success" icon={faCircleExclamation} size="2xl" />
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="alert alert-success">Successfully Created User!</div>
                    <p><strong>Email:</strong> {createdEmail}</p>
                    <p>
                        <strong>Temporary Password:</strong> {createdPassword}
                        <FontAwesomeIcon
                            className="ms-3 text-secondary"
                            icon={faCopy}
                            onClick={handleCopyPassword}
                            style={{ cursor: 'pointer' }}
                        />
                        <br />
                        {copyMessage && <span className="text-success">{copyMessage}</span>}
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowSuccessModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default NewUser;
